import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight,
  Smartphone,
  CheckCircle,
  User,
  ArrowLeft,
  MessageCircle,
  Clock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import toast from 'react-hot-toast';

const UserLogin = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('phone'); // 'phone', 'otp', 'name'
  const [loading, setLoading] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  
  // Form state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [userName, setUserName] = useState('');

  // Validation state
  const [errors, setErrors] = useState({});

  // Direct Fast2SMS WhatsApp OTP Integration
  const sendWhatsAppOTPDirect = async (phoneNumber, otp) => {
    try {
      // Format phone number (ensure it starts with +91)
      let formattedNumber = phoneNumber;
      if (!phoneNumber.startsWith('+91')) {
        formattedNumber = phoneNumber.startsWith('91') ? `+${phoneNumber}` : `+91${phoneNumber}`;
      }

      // Fast2SMS API configuration
      const API_KEY = '8n4kQedfONjo1wVxEBKHFcaI3MYs9UhSbJG026R5vmyDritpLgzbCykpM029uaOvLUAd3XVhYGJclHSe';
      const BASE_URL = 'https://www.fast2sms.com/dev/whatsapp';
      const MESSAGE_ID = '6231'; // login otp template
      
      // Generate API URL
      const apiUrl = `${BASE_URL}?authorization=${API_KEY}&message_id=${MESSAGE_ID}&numbers=${formattedNumber}&variables_values=${otp}|10`;

      console.log('📱 Sending WhatsApp OTP to:', formattedNumber);
      console.log('🔗 API URL:', apiUrl.replace(API_KEY, 'HIDDEN_API_KEY'));

      // Send request using fetch
      const response = await fetch(apiUrl);
      const data = await response.json();
      
      console.log('📊 Fast2SMS Response:', data);
      
      if (data && data.return === true) {
        return {
          success: true,
          message: 'OTP sent successfully',
          requestId: data.request_id
        };
      } else {
        throw new Error(data.message || 'Failed to send OTP');
      }
    } catch (error) {
      console.error('WhatsApp OTP Error:', error);
      throw new Error('Failed to send WhatsApp OTP');
    }
  };

  // OTP Timer
  React.useEffect(() => {
    let interval = null;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer(timer => timer - 1);
      }, 1000);
    } else if (otpTimer === 0) {
      setOtpSent(false);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  // Validation functions
  const validatePhone = (phone) => {
    const phoneRegex = /^[0-9]{10}$/;
    if (!phone.trim()) {
      return 'Mobile number is required';
    }
    if (!phoneRegex.test(phone)) {
      return 'Mobile number should be exactly 10 digits';
    }
    return '';
  };

  const validateName = (name) => {
    const nameRegex = /^[a-zA-Z\s]+$/;
    if (!name.trim()) {
      return 'Full name is required';
    }
    if (!nameRegex.test(name)) {
      return 'Name should only contain alphabets and spaces';
    }
    if (name.trim().length < 2) {
      return 'Name should be at least 2 characters long';
    }
    return '';
  };

  const validateOTP = (otp) => {
    const otpRegex = /^[0-9]{6}$/;
    if (!otp.trim()) {
      return 'OTP is required';
    }
    if (!otpRegex.test(otp)) {
      return 'OTP should be exactly 6 digits';
    }
    return '';
  };

  // Send WhatsApp OTP
  const sendOTP = async () => {
    setLoading(true);
    try {
      console.log('🔍 Checking user existence for phone:', phoneNumber);
      
      // Check if user exists
      const userQuery = query(
        collection(db, 'users'),
        where('phone', '==', phoneNumber)
      );
      const userSnapshot = await getDocs(userQuery);
      
      console.log('📊 User query result:', userSnapshot.empty ? 'No user found' : 'User found');
      
      if (userSnapshot.empty) {
        setIsNewUser(true);
        console.log('👤 New user detected');
      } else {
        setIsNewUser(false);
        console.log('👤 Existing user detected:', userSnapshot.docs[0].data().name);
      }

      // Generate OTP
      const otp = Math.floor(100000 + Math.random() * 900000).toString();
      
      // Send WhatsApp OTP via Direct API
      console.log('📱 Sending WhatsApp OTP to:', phoneNumber);
      const result = await sendWhatsAppOTPDirect(phoneNumber, otp);
      
      console.log('✅ WhatsApp OTP sent successfully:', result);
      
      // Store OTP in localStorage for verification
      localStorage.setItem('otp_' + phoneNumber, JSON.stringify({
        otp: otp,
        timestamp: Date.now(),
        expiresAt: Date.now() + (10 * 60 * 1000) // 10 minutes
      }));
      
      // Set OTP sent state and start timer
      setOtpSent(true);
      setOtpTimer(600); // 10 minutes timer
      
      toast.success(`WhatsApp OTP sent to +91${phoneNumber}! Check your WhatsApp messages.`);
      setStep('otp');
      
    } catch (error) {
      console.error('❌ Error sending WhatsApp OTP:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      // Fallback to demo OTP for development
      if (error.code === 'functions/unavailable' || error.message.includes('Firebase')) {
        console.log('🔄 Falling back to demo OTP');
        setOtpSent(true);
        setOtpTimer(600);
        toast.success(`Demo OTP sent to +91${phoneNumber}! Use: 123456`);
        setStep('otp');
      } else {
        toast.error('Failed to send WhatsApp OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Verify WhatsApp OTP (Direct verification)
  const verifyOTP = async () => {
    setLoading(true);
    try {
      console.log('🔐 Verifying WhatsApp OTP:', otp);
      
      // Get stored OTP data
      const storedOTPData = localStorage.getItem('otp_' + phoneNumber);
      
      if (!storedOTPData) {
        throw new Error('OTP not found or expired');
      }
      
      const otpData = JSON.parse(storedOTPData);
      
      // Check if OTP is expired
      if (Date.now() > otpData.expiresAt) {
        localStorage.removeItem('otp_' + phoneNumber);
        throw new Error('OTP has expired');
      }
      
      // Verify OTP
      if (otpData.otp === otp) {
        console.log('✅ WhatsApp OTP verified successfully');
        toast.success('OTP verified successfully!');
        
        // Clear stored OTP
        localStorage.removeItem('otp_' + phoneNumber);
        
        if (isNewUser) {
          setStep('name');
        } else {
          // Existing user - login directly
          await loginExistingUser();
        }
      } else {
        throw new Error('Invalid OTP');
      }
      
    } catch (error) {
      console.error('❌ Error verifying WhatsApp OTP:', error);
      
      // Fallback to demo verification for development
      if (error.message.includes('OTP not found') || error.message.includes('expired')) {
        console.log('🔄 Falling back to demo OTP verification');
        
        // Demo OTP verification - accept 123456 or any 6-digit number
        if (otp === '123456' || (otp.length === 6 && /^[0-9]{6}$/.test(otp))) {
          toast.success('Demo OTP verified successfully!');
          
          if (isNewUser) {
            setStep('name');
          } else {
            // Existing user - login directly
            await loginExistingUser();
          }
        } else {
          toast.error('Invalid OTP. Demo OTP is: 123456');
        }
      } else {
        toast.error(error.message || 'Failed to verify OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Resend WhatsApp OTP
  const resendOTP = async () => {
    if (otpTimer > 0) {
      toast.error(`Please wait ${Math.floor(otpTimer / 60)}:${(otpTimer % 60).toString().padStart(2, '0')} before resending OTP`);
      return;
    }
    
    setLoading(true);
    try {
      console.log('🔄 Resending WhatsApp OTP to:', phoneNumber);
      
      // Generate new OTP
      const otp = Math.floor(100000 + Math.random() * 900000).toString();
      
      const result = await sendWhatsAppOTPDirect(phoneNumber, otp);
      
      console.log('✅ WhatsApp OTP resent successfully:', result);
      
      // Store new OTP in localStorage
      localStorage.setItem('otp_' + phoneNumber, JSON.stringify({
        otp: otp,
        timestamp: Date.now(),
        expiresAt: Date.now() + (10 * 60 * 1000) // 10 minutes
      }));
      
      setOtpSent(true);
      setOtpTimer(600); // 10 minutes timer
      
      toast.success(`WhatsApp OTP resent to +91${phoneNumber}!`);
      
    } catch (error) {
      console.error('❌ Error resending WhatsApp OTP:', error);
      
      // Fallback to demo OTP for development
      console.log('🔄 Falling back to demo OTP resend');
      setOtpSent(true);
      setOtpTimer(600);
      toast.success(`Demo OTP resent to +91${phoneNumber}! Use: 123456`);
    } finally {
      setLoading(false);
    }
  };

  // Login existing user
  const loginExistingUser = async () => {
    try {
      const userQuery = query(
        collection(db, 'users'),
        where('phone', '==', phoneNumber)
      );
      const userSnapshot = await getDocs(userQuery);
      
      if (!userSnapshot.empty) {
        const userData = userSnapshot.docs[0].data();
        
        // Store user data in localStorage
        localStorage.setItem('userData', JSON.stringify({
          id: userSnapshot.docs[0].id,
          ...userData,
          isLoggedIn: true
        }));
        localStorage.setItem('customerPhone', phoneNumber);
        
        toast.success('Login successful!');
        
        // Navigate to order page
        setTimeout(() => {
          navigate('/order');
        }, 1000);
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed. Please try again.');
    }
  };

  // Register new user
  const registerNewUser = async () => {
    setLoading(true);
    try {
      // Add user to Firestore
      const userData = {
        name: userName.trim(),
        phone: phoneNumber,
        createdAt: new Date(),
        isActive: true
      };
      
      const docRef = await addDoc(collection(db, 'users'), userData);
      
      // Store user data in localStorage
      localStorage.setItem('userData', JSON.stringify({
        id: docRef.id,
        ...userData,
        isLoggedIn: true
      }));
      localStorage.setItem('customerPhone', phoneNumber);
      
      toast.success('Registration successful!');
      
      // Navigate to order page
      setTimeout(() => {
        navigate('/order');
      }, 1000);
      
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSubmit = (e) => {
    e.preventDefault();
    const phoneError = validatePhone(phoneNumber);
    if (phoneError) {
      setErrors({ phone: phoneError });
      return;
    }
    setErrors({});
    sendOTP();
  };

  const handleOTPSubmit = (e) => {
    e.preventDefault();
    const otpError = validateOTP(otp);
    if (otpError) {
      setErrors({ otp: otpError });
      return;
    }
    setErrors({});
    verifyOTP();
  };

  const handleNameSubmit = (e) => {
    e.preventDefault();
    const nameError = validateName(userName);
    if (nameError) {
      setErrors({ name: nameError });
      return;
    }
    setErrors({});
    registerNewUser();
  };

  const goBack = () => {
    if (step === 'otp') {
      setStep('phone');
      setOtp('');
    } else if (step === 'name') {
      setStep('otp');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '40px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          width: '100%',
          maxWidth: '400px'
        }}
      >
        {/* Demo Credentials */}
        <div style={{
          backgroundColor: '#f0f9ff',
          border: '1px solid #0ea5e9',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px'
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '700',
            color: '#0c4a6e',
            margin: '0 0 12px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
              borderRadius: '6px',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Smartphone size={16} color="white" />
            </div>
            Demo Credentials
          </h3>
          
          <div style={{ fontSize: '14px', color: '#0c4a6e', lineHeight: '1.5' }}>
            <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>For Testing:</p>
            <div style={{ marginLeft: '12px', marginBottom: '8px' }}>
              <p style={{ margin: '2px 0' }}>• <strong>8107436861</strong> - Contact us directly</p>
            </div>
            <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>Demo OTP: <strong>123456</strong></p>
            <p style={{ margin: '0', fontSize: '12px', color: '#64748b' }}>
              💡 Use any 10-digit number for new user registration
            </p>
          </div>
        </div>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            boxShadow: '0 8px 16px rgba(255, 107, 53, 0.3)'
          }}>
            {step === 'phone' && <Smartphone size={32} color="white" />}
            {step === 'otp' && <CheckCircle size={32} color="white" />}
            {step === 'name' && <User size={32} color="white" />}
          </div>
          
          <h1 style={{
            fontSize: '24px',
            fontWeight: '800',
            color: '#1f2937',
            margin: '0 0 8px 0'
          }}>
            {step === 'phone' && 'Welcome to B.Tech Bawarchi'}
            {step === 'otp' && 'Verify WhatsApp OTP'}
            {step === 'name' && 'Complete Your Profile'}
          </h1>
          
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            margin: 0
          }}>
            {step === 'phone' && 'Enter your mobile number to get started'}
            {step === 'otp' && `We sent a WhatsApp OTP to +91${phoneNumber}. Check your WhatsApp messages.`}
            {step === 'name' && 'Please provide your name to complete registration'}
          </p>
        </div>

        {/* Back Button */}
        {(step === 'otp' || step === 'name') && (
          <button
            onClick={goBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: 'transparent',
              border: 'none',
              color: '#6b7280',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              marginBottom: '24px',
              padding: '8px',
              borderRadius: '8px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
              e.currentTarget.style.color = '#374151';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#6b7280';
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        )}

        {/* Phone Number Form */}
        {step === 'phone' && (
          <form onSubmit={handlePhoneSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                Mobile Number *
              </Label>
              <div style={{ position: 'relative' }}>
                <div style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#6b7280',
                  fontSize: '16px',
                  fontWeight: '500',
                  zIndex: 1
                }}>
                  <Smartphone style={{ width: '20px', height: '20px' }} />
                  <span>+91</span>
                </div>
                <Input
                  type="tel"
                  placeholder="Enter 10-digit mobile number"
                  value={phoneNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                    setPhoneNumber(value);
                  }}
                  required
                  maxLength="10"
                  style={{
                    padding: '14px 14px 14px 70px',
                    borderRadius: '10px',
                    border: errors.phone ? '2px solid #ef4444' : '2px solid #e5e7eb',
                    fontSize: '16px',
                    width: '100%',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                  onBlur={(e) => e.target.style.borderColor = errors.phone ? '#ef4444' : '#e5e7eb'}
                />
              </div>
              {errors.phone && (
                <p style={{ color: '#ef4444', fontSize: '12px', margin: '4px 0 0 0' }}>
                  {errors.phone}
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading || phoneNumber.length !== 10}
              style={{
                background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                color: 'white',
                border: 'none',
                padding: '14px',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading || phoneNumber.length !== 10 ? 'not-allowed' : 'pointer',
                opacity: loading || phoneNumber.length !== 10 ? 0.6 : 1,
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {loading ? 'Sending...' : 'Send OTP'}
              {!loading && <ArrowRight size={18} />}
            </Button>
          </form>
        )}

        {/* OTP Verification Form */}
        {step === 'otp' && (
          <form onSubmit={handleOTPSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                Enter OTP *
              </Label>
              <Input
                type="tel"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                  setOtp(value);
                }}
                required
                maxLength="6"
                style={{
                  padding: '14px',
                  borderRadius: '10px',
                  border: errors.otp ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  fontSize: '16px',
                  width: '100%',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  textAlign: 'center',
                  letterSpacing: '2px'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = errors.otp ? '#ef4444' : '#e5e7eb'}
              />
              {errors.otp && (
                <p style={{ color: '#ef4444', fontSize: '12px', margin: '4px 0 0 0' }}>
                  {errors.otp}
                </p>
              )}
            </div>

            {/* WhatsApp OTP Info */}
            <div style={{
              backgroundColor: '#f0f9ff',
              border: '1px solid #0ea5e9',
              borderRadius: '8px',
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px'
            }}>
              <MessageCircle size={16} color="#0ea5e9" />
              <div style={{ fontSize: '12px', color: '#0369a1' }}>
                <strong>WhatsApp OTP:</strong> Check your WhatsApp messages from Btech Bawarchi (+918107436861)
              </div>
            </div>

            {/* OTP Timer */}
            {otpTimer > 0 && (
              <div style={{
                backgroundColor: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '8px',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <Clock size={16} color="#f59e0b" />
                <div style={{ fontSize: '12px', color: '#92400e' }}>
                  <strong>OTP expires in:</strong> {Math.floor(otpTimer / 60)}:{(otpTimer % 60).toString().padStart(2, '0')}
                </div>
              </div>
            )}

            {/* Resend OTP Button */}
            {otpTimer === 0 && (
              <button
                type="button"
                onClick={resendOTP}
                disabled={loading}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid #ff6b35',
                  color: '#ff6b35',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                  transition: 'all 0.2s ease',
                  alignSelf: 'flex-start'
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor = '#ff6b35';
                    e.currentTarget.style.color = 'white';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#ff6b35';
                  }
                }}
              >
                Resend WhatsApp OTP
              </button>
            )}

            <Button
              type="submit"
              disabled={loading || otp.length !== 6}
              style={{
                background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                color: 'white',
                border: 'none',
                padding: '14px',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading || otp.length !== 6 ? 'not-allowed' : 'pointer',
                opacity: loading || otp.length !== 6 ? 0.6 : 1,
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {loading ? 'Verifying...' : 'Verify OTP'}
              {!loading && <CheckCircle size={18} />}
            </Button>
          </form>
        )}

        {/* Name Collection Form */}
        {step === 'name' && (
          <form onSubmit={handleNameSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                Full Name *
              </Label>
              <Input
                type="text"
                placeholder="Enter your full name"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                required
                style={{
                  padding: '14px',
                  borderRadius: '10px',
                  border: errors.name ? '2px solid #ef4444' : '2px solid #e5e7eb',
                  fontSize: '16px',
                  width: '100%',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = errors.name ? '#ef4444' : '#e5e7eb'}
              />
              {errors.name && (
                <p style={{ color: '#ef4444', fontSize: '12px', margin: '4px 0 0 0' }}>
                  {errors.name}
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading || !userName.trim()}
              style={{
                background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                color: 'white',
                border: 'none',
                padding: '14px',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading || !userName.trim() ? 'not-allowed' : 'pointer',
                opacity: loading || !userName.trim() ? 0.6 : 1,
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {loading ? 'Creating Account...' : 'Complete Registration'}
              {!loading && <ArrowRight size={18} />}
            </Button>
          </form>
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default UserLogin;